/**
 * The parse-and-execute interleave (R21) — each clause reads the world its
 * predecessors left.
 *
 * @remarks
 * Before R21, the whole input parsed ONCE against the input-start catalog, so
 * `south and take the pry bar and north` failed at clause two: the bar was
 * one room ahead of where the catalog was drawn (D23's recorded v0
 * limitation). Now the initial whole-input parse is kept for what it alone
 * can do — segmentation (clause texts and boundaries, the D80 retry-split
 * included) and the FIRST clause's reading — and every clause after the
 * first re-parses its own folded text against the catalog derived from the
 * state its predecessors just committed. A mid-chain clarification therefore
 * cites candidates from the CURRENT room, and a chain behaves exactly as
 * typing its clauses one input at a time, minus the extra echoes.
 *
 * Determinism holds by construction: every re-parse is a pure function of
 * committed state, so `(bundle, seed, raw commands)` replays byte-exact.
 *
 * In-input pronoun recency mirrors the parser's own threading (parse-command's
 * walk): each executed clause's resolved refs — direct object, bound phrase,
 * tool — prepend to the view the next clause's re-parse receives, ahead of
 * the caller's cross-input list, so `take the key then use it on the door`
 * resolves "it" identically whether the clauses share a parse call or not.
 *
 * A re-parse may itself propose several clauses (a D80 merged clause,
 * re-split once the world changed): its first command executes and the rest
 * demote to texts, so the law applies recursively — no clause ever executes
 * from a reading older than the state it runs against.
 *
 * @packageDocumentation
 */

import type { RuntimeState, RuntimeTranscriptEntry } from '@orpheus/content-schema';

import {
	type ClarificationRequest,
	type EntityRef,
	type ParseCandidateEntity,
	parseCommand,
	type ParsedCommand,
	type ParseDiagnostics,
	type ParseOutcome,
	type PronounAntecedents,
	type TokenCorrection,
} from '@orpheus/parser-engine';

import type { ActionRejection } from '../actions/runtime-action.js';
import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';

import { type SpokenResponse } from '../defaults/default-responses.js';
import { deriveEntityCatalog } from '../entities/derive-entity-catalog.js';
import { deriveTopicCatalog, isTopicRef } from '../entities/derive-topic-catalog.js';
import { type ClauseWalk, executeClauseOntoWalk, openClauseWalk } from './clause-walk.js';
import { pronounAntecedentsOf } from './conversational-partner.js';
import { buildParseContext } from './parse-context.js';
import { type AuthoredReaction, authoredReactionFor, reactionKeyOf } from './parse-failure-arm.js';

/**
 * How walking an input's clauses ended. Every arm carries the walk (state and
 * entries committed through the stop) and the corrections of every clause the
 * parser actually READ — the clauses beyond a stop were never parsed, so they
 * contribute none.
 */
export type InputWalkResult =
	| {
		/**
		 * D226 (E6): a clause that parsed as a declared-unsupported verb with
		 * nothing to act on — `listen`, `smell` — under a word the pack wrote
		 * a reaction for. The reaction is the answer; the caller speaks it
		 * through the same tail a parse-failure reaction takes.
		 */
		readonly corrections: readonly TokenCorrection[];
		readonly reaction: AuthoredReaction;
		/** The folded texts of clauses after the answered one, never run (§8.8's coda). */
		readonly remainderTexts: readonly string[];
		readonly type: 'answered_by_reaction';
		readonly walk: ClauseWalk;
	}
	| {
		readonly clarification: ClarificationRequest;
		readonly corrections: readonly TokenCorrection[];
		/** The folded texts of clauses after the asking one, never run (§8.8's coda). */
		readonly remainderTexts: readonly string[];
		readonly type: 'stopped_for_clarification';
		readonly walk: ClauseWalk;
	}
	| {
		readonly corrections: readonly TokenCorrection[];
		/**
		 * The refusal PAIRED with its clause (F5) — present by construction
		 * (lifted off the walk, whose optional field cannot promise it), with
		 * the narratives and §8.8 coda already committed on the walk.
		 */
		readonly rejection: { readonly clauseText: string; readonly rejection: ActionRejection };
		readonly type: 'rejected';
		readonly walk: ClauseWalk;
	}
	| {
		readonly corrections: readonly TokenCorrection[];
		/** Every clause that consumed a turn, in execution order (D67 reads its refs). */
		readonly executedCommands: readonly ParsedCommand[];
		readonly type: 'completed';
		readonly walk: ClauseWalk;
	}
	| {
		readonly corrections: readonly TokenCorrection[];
		readonly diagnostics: ParseDiagnostics;
		/** The folded texts of clauses after the failing one, never run (§8.8's coda). */
		readonly remainderTexts: readonly string[];
		readonly type: 'stopped_for_parse_failure';
		readonly walk: ClauseWalk;
	};

/**
 * What one parse contributes to the walk: the first proposed clause ready to
 * execute (absent when the parse proposed none), the texts of everything
 * behind it (demoted for later re-parse), or a stop the walker surfaces.
 */
type AbsorbedParse =
	| {
		readonly demotedTexts: readonly string[];
		readonly firstCommand: ParsedCommand | undefined;
		readonly stop?: undefined;
	}
	| { readonly stop: ParseStop };

/** A parse-level stop: the FIRST unexecuted clause asked or failed. */
type ParseStop =
	| {
		readonly clarification: ClarificationRequest;
		readonly kind: 'clarification';
		readonly remainderTexts: readonly string[];
	}
	| {
		readonly diagnostics: ParseDiagnostics;
		readonly kind: 'parse_failure';
		readonly remainderTexts: readonly string[];
	};

/**
 * Walk an input's clauses: execute the initial parse's first clause, then
 * alternate re-parse and execute until the clauses are exhausted or one of
 * them stops the walk (a refusal, a clarification, a parse failure, or a
 * terminal ending).
 *
 * @param bundle - The loaded bundle
 * @param openedState - The state WITH history + echo already committed
 * @param initialParse - The whole-input parse (segmentation + the first clause's reading)
 * @param inputOrdinal - This input's ordinal (id namespace)
 * @param echoEntries - The player-echo entries (head of this input's entry list)
 * @param sessionRecencyRefs - The cross-input pronoun view (D67), threaded behind in-input refs
 * @param rejectionOverride - §3.2 (D84, scoped by D91): re-voice an ANSWER's refusal in the question's terms — applied to the FIRST clause only, because a splice puts the answer there and later clauses are the player's own chain
 * @returns How the walk ended, state and entries committed on every arm
 */
export const walkInputClauses = (
	bundle: EngineGameBundle,
	openedState: RuntimeState,
	initialParse: ParseOutcome,
	inputOrdinal: number,
	echoEntries: readonly RuntimeTranscriptEntry[],
	sessionRecencyRefs: readonly EntityRef[],
	rejectionOverride?: (rejection: ActionRejection) => SpokenResponse | undefined,
): InputWalkResult => {
	let walk = openClauseWalk(openedState, echoEntries);
	let threadedRecencyRefs: readonly EntityRef[] = [];
	let pendingTexts: readonly string[] = [];
	let readyCommand: ParsedCommand | undefined = undefined;
	// D217: the members of a set the pack answers one by one, queued ahead of every text still to parse.
	let queuedMemberCommands: readonly ParsedCommand[] = [];
	const executedCommands: ParsedCommand[] = [];
	const corrections: TokenCorrection[] = [];

	const absorbed = absorbParseAskingAboutTopics(initialParse, bundle, walk.state, sessionRecencyRefs);

	if (absorbed.stop !== undefined) {
		return stopResult(absorbed.stop, corrections, walk);
	}

	({ demotedTexts: pendingTexts, firstCommand: readyCommand } = absorbed);

	for (;;) {
		if (readyCommand === undefined) {
			const [nextMember, ...laterMembers] = queuedMemberCommands;

			if (nextMember !== undefined) {
				readyCommand = nextMember;
				queuedMemberCommands = laterMembers;
				continue;
			}

			const [nextText, ...restTexts] = pendingTexts;

			// Texts exhausted: every clause ran (the happy path).
			if (nextText === undefined) {
				return { corrections, executedCommands, type: 'completed', walk };
			}

			// The R21 re-parse: this clause's catalog is derived from the state
			// its predecessors just committed — never the input-start state.
			const reParse = parseCommand(
				nextText,
				buildParseContext(bundle, deriveEntityCatalog(bundle, walk.state), {
					...pronounViewOf(bundle, walk.state),
					recentEntityRefs: [...threadedRecencyRefs, ...sessionRecencyRefs],
				}),
			);
			const reAbsorbed = absorbParseAskingAboutTopics(reParse, bundle, walk.state, [...threadedRecencyRefs, ...sessionRecencyRefs]);

			if (reAbsorbed.stop !== undefined) {
				// The stop's own inner remainder (clauses within a merged text)
				// precedes the texts still queued behind it, in input order.
				return stopResult(
					{ ...reAbsorbed.stop, remainderTexts: [...reAbsorbed.stop.remainderTexts, ...restTexts]},
					corrections,
					walk,
				);
			}

			({ firstCommand: readyCommand } = reAbsorbed);
			pendingTexts = [...reAbsorbed.demotedTexts, ...restTexts];
			continue;
		}

		// D217: a SET the pack answers member by member becomes one command per
		// member, in the parser's order, each a turn of its own — exactly as if
		// the player had typed the command once per thing. The members were
		// read against this clause's world; each still re-validates its own
		// thing when it narrows, so one that has gone since is refused, and the
		// refusal stops the walk like any other.
		if (readyCommand.directObjectSetRefs !== undefined && answersEachMember(bundle, readyCommand.directObjectSetRefs.length)) {
			queuedMemberCommands = [...memberCommandsOf(readyCommand, readyCommand.directObjectSetRefs), ...queuedMemberCommands];
			readyCommand = undefined;
			continue;
		}

		// The refused clause was still READ — its corrections are part of how
		// the parser understood the input, so they accrue before the turn runs.
		corrections.push(...readyCommand.corrections);

		// D226 (E6): a declared-unsupported verb standing alone is a refusal
		// waiting to be spoken from a slot; when the pack wrote a reaction under
		// that very word, the reaction is the better answer. Read before the
		// turn runs, because the turn would commit the refusal.
		const bareVerbReaction = reactionForBareUnsupportedVerb(bundle, readyCommand);

		if (bareVerbReaction !== undefined) {
			return { corrections, reaction: bareVerbReaction, remainderTexts: pendingTexts, type: 'answered_by_reaction', walk };
		}

		// D91: the answer-context override belongs to the ANSWER, and a splice
		// puts the answer in the FIRST clause — a later clause is the player's
		// own chain, and its refusal speaks in its own voice, never the parked
		// question's terms.
		const step = executeClauseOntoWalk(
			bundle,
			walk,
			readyCommand,
			inputOrdinal,
			pendingTexts,
			executedCommands.length === 0 ? rejectionOverride : undefined,
		);

		walk = step.walk;

		if (walk.rejection !== undefined) {
			return { corrections, rejection: walk.rejection, type: 'rejected', walk };
		}

		executedCommands.push(readyCommand);

		// R11: a terminal ending closes the input — the remaining clauses go
		// unread, exactly as a next input would.
		if (step.stopped) {
			return { corrections, executedCommands, type: 'completed', walk };
		}

		// Mirror of parse-command's in-input threading (resolvedRefsOf → prepend):
		// the clause just executed becomes the next re-parse's freshest "it".
		threadedRecencyRefs = [
			...[readyCommand.directObjectRef, readyCommand.indirectObjectRef, readyCommand.toolRef].filter(
				(entityRef): entityRef is EntityRef => entityRef !== undefined,
			),
			...threadedRecencyRefs,
		];
		readyCommand = undefined;
	}
};

/**
 * D226 (E6): the pack's reaction for a clause that is a declared-unsupported
 * verb and nothing else — no object in any slot, no direction, no set.
 *
 * @remarks
 * The POC authors `reactions.listen` and `reactions.smell`, and the standard
 * vocabulary declares both verbs unsupported so that `listen to the pipes`
 * can be refused by name (D35). The parse therefore SUCCEEDED, the reaction
 * table was never consulted (it is read only on a whole-input failure), and
 * every authored line under a declared verb was unreachable. A verb the
 * roster cannot act on, aimed at nothing, is exactly the input D27 meant the
 * table for; with an object it stays the refusal, which names the thing.
 *
 * @param bundle - The loaded bundle
 * @param command - The clause about to run
 * @returns The reaction, or `undefined` when the clause is anything else or the pack wrote none
 */
/**
 * D235: the conversational partner's pronoun view for a parse against this
 * state — an extras fragment, empty when nobody in earshot is being talked with.
 *
 * @param bundle - The loaded bundle
 * @param state - The state the clause parses against
 * @returns The `pronounAntecedents` extra, or nothing
 */
const pronounViewOf = (bundle: EngineGameBundle, state: RuntimeState): { readonly pronounAntecedents?: PronounAntecedents } => {
	const antecedents = pronounAntecedentsOf(bundle, state);

	return antecedents === undefined ? {} : { pronounAntecedents: antecedents };
};

const reactionForBareUnsupportedVerb = (bundle: EngineGameBundle, command: ParsedCommand): AuthoredReaction | undefined => {
	const isBareVerb =
		command.directObjectRef === undefined &&
		command.directObjectSetRefs === undefined &&
		command.indirectObjectRef === undefined &&
		command.toolRef === undefined &&
		command.direction === undefined;

	if (!isBareVerb || bundle.compiledLexicon.intentsByToken.get(command.intent)?.isUnsupported !== true) {
		return undefined;
	}

	return authoredReactionFor(bundle, reactionKeyOf(command.clauseText));
};

/**
 * D217: does this pack answer a set of this size member by member? `each`
 * always; `each_up_to` while the set is no bigger than its count; `set`
 * never — narrowing answers it in one line.
 *
 * @param bundle - The loaded bundle, whose `plurals` is the builder's choice
 * @param memberCount - How many things the set holds
 * @returns Whether to split the set into one command per member
 */
const answersEachMember = (bundle: EngineGameBundle, memberCount: number): boolean => {
	const { plurals } = bundle.contentPack;

	return plurals.answer === 'each' || (plurals.answer === 'each_up_to' && memberCount <= plurals.count);
};

/**
 * D217: one command per member of a set — the set's command, pointed at each
 * member alone.
 *
 * @param setCommand - The command whose direct object is the set
 * @param memberRefs - The members, in the parser's order
 * @returns The single-object commands, in the same order
 */
const memberCommandsOf = (setCommand: ParsedCommand, memberRefs: readonly EntityRef[]): readonly ParsedCommand[] => {
	const { directObjectSetRefs: _theSet, ...singleObjectCommand } = setCommand;

	return memberRefs.map((memberRef) => ({ ...singleObjectCommand, directObjectRef: memberRef }));
};

/**
 * Read one parse outcome into the walker's terms. A non-empty prefix means
 * the parse's FIRST clause is ready to execute and everything behind it —
 * later prefix clauses, the asking/failing clause, the never-parsed tail —
 * demotes to text for later re-parse (the R21 law applied recursively). An
 * empty prefix means the first unexecuted clause itself asked or failed, and
 * the walk stops with it.
 *
 * @param parse - One parse outcome (the initial whole-input parse, or a re-parse)
 * @returns What the parse contributes: a ready command + demoted texts, or a stop
 */
const absorbParse = (parse: ParseOutcome): AbsorbedParse => {
	if (parse.type === 'parsed') {
		const [firstCommand, ...laterCommands] = parse.commands;

		return { demotedTexts: laterCommands.map((command) => command.clauseText), firstCommand };
	}

	const [prefixHead, ...prefixRest] = parse.parsedPrefix;
	const remainderTexts = parse.remainingClauseTexts ?? [];
	const stoppingClauseTexts =
		parse.type === 'needs_clarification'
			? [parse.clarification.clauseText]
			: parse.diagnostics.failedClauseText === undefined
				? []
				: [parse.diagnostics.failedClauseText];

	if (prefixHead !== undefined) {
		return {
			demotedTexts: [...prefixRest.map((command) => command.clauseText), ...stoppingClauseTexts, ...remainderTexts],
			firstCommand: prefixHead,
		};
	}

	return {
		stop:
			parse.type === 'needs_clarification'
				? { clarification: parse.clarification, kind: 'clarification', remainderTexts }
				: { diagnostics: parse.diagnostics, kind: 'parse_failure', remainderTexts },
	};
};

/**
 * Read one parse into the walker's terms, and — when its first unexecuted
 * clause failed on a thing that is not here — read that clause once more
 * with the player's TOPICS in the catalog (D226, E7).
 *
 * @remarks
 * `ask Sylvain about the drum` in the hollow failed as `entity_not_found`:
 * the drum is in the clearing, and the per-turn catalog lists what is here.
 * The second reading parses the same clause against the present catalog
 * plus the topic catalog — the things the player has met in the rooms they
 * have walked, each marked `absent` — and is adopted ONLY as an `ask` whose
 * bound `about` phrase is one of those topics, every other reference
 * present. Anything else the wider catalog would read (`take the drum`,
 * `talk to the boy` with the boy elsewhere, a which-one among absent jars)
 * keeps the honest miss the first reading earned: a topic is the one thing
 * a sentence may be about without reaching it. Two parses on a miss, none
 * on a hit; deterministic by construction.
 *
 * @param parse - One parse outcome (the initial whole-input parse, or a re-parse)
 * @param bundle - The loaded bundle
 * @param state - The state the clause is read against
 * @param recencyRefs - The pronoun view for the second reading
 * @returns What the parse contributes, the topic reading adopted where it applies
 */
const absorbParseAskingAboutTopics = (
	parse: ParseOutcome,
	bundle: EngineGameBundle,
	state: RuntimeState,
	recencyRefs: readonly EntityRef[],
): AbsorbedParse => {
	const absorbed = absorbParse(parse);

	if (absorbed.stop === undefined || absorbed.stop.kind !== 'parse_failure' || absorbed.stop.diagnostics.failureKind !== 'entity_not_found') {
		return absorbed;
	}

	const failedClauseText = absorbed.stop.diagnostics.failedClauseText;

	// The parser names the failing clause on every per-clause failure; only
	// blank input and the clause cap go unnamed, and neither is an entity
	// miss. Typed optional, so the guard stays; no input reaches it.
	/* v8 ignore next 3 */
	if (failedClauseText === undefined) {
		return absorbed;
	}

	const presentCatalog = deriveEntityCatalog(bundle, state);
	const topics = deriveTopicCatalog(bundle, state, presentCatalog);

	if (topics.length === 0) {
		return absorbed;
	}

	const secondReading = parseCommand(
		failedClauseText,
		buildParseContext(bundle, [...presentCatalog, ...topics], { ...pronounViewOf(bundle, state), recentEntityRefs: recencyRefs }),
	);
	const [askAboutTopic, ...furtherCommands] = secondReading.type === 'parsed' ? secondReading.commands : [];

	if (askAboutTopic === undefined || furtherCommands.length > 0 || !isAskAboutTopic(askAboutTopic, topics)) {
		return absorbed;
	}

	return { demotedTexts: absorbed.stop.remainderTexts, firstCommand: askAboutTopic };
};

/**
 * Is this command an `ask` whose topic — its bound phrase — is absent, with
 * everything else it names present (D226, E7)?
 *
 * @param command - The second reading's command
 * @param topics - The topic catalog
 * @returns Whether the reading may be adopted
 */
const isAskAboutTopic = (command: ParsedCommand, topics: readonly ParseCandidateEntity[]): boolean => {
	const intentText: string = command.intent;

	return (
		intentText === 'ask' &&
		command.indirectObjectRef !== undefined &&
		isTopicRef(topics, command.indirectObjectRef) &&
		command.directObjectSetRefs === undefined &&
		[command.directObjectRef, command.toolRef].every((otherRef) => otherRef === undefined || !isTopicRef(topics, otherRef))
	);
};

/**
 * Surface a parse-level stop as the walk's result.
 *
 * @param stop - The stop (the first unexecuted clause asked or failed)
 * @param corrections - The corrections accrued through the stop
 * @param walk - The walk as committed through the stop
 * @returns The clarification or parse-failure arm
 */
const stopResult = (stop: ParseStop, corrections: readonly TokenCorrection[], walk: ClauseWalk): InputWalkResult =>
	stop.kind === 'clarification'
		? {
			clarification: stop.clarification,
			corrections,
			remainderTexts: stop.remainderTexts,
			type: 'stopped_for_clarification',
			walk,
		}
		: {
			corrections,
			diagnostics: stop.diagnostics,
			remainderTexts: stop.remainderTexts,
			type: 'stopped_for_parse_failure',
			walk,
		};
